<template>
  <div class="spreadsheet-shell">
    <div class="spreadsheet-frame">
      <div class="spreadsheet-container" ref="spreadsheetContainer"></div>
    </div>

    <div v-if="showAddRowsButton" class="sheet-footer">
      <button class="sheet-footer__action" type="button" @click="addRows">
        {{ buttonLabel }}
      </button>
    </div>
  </div>
</template>

<script>
import Handsontable from 'handsontable';
import { HyperFormula } from 'hyperformula';
import 'handsontable/dist/handsontable.full.css';
import { fillDownSelection, captureSpreadsheetState, applySpreadsheetState, normalizeGridDataForHandsontable } from '../utils/SpreadsheetUtils';

const DEFAULT_COL_WIDTH = 100;
const DEFAULT_ROW_HEIGHT = 23;
const WRAPPED_ROW_HEIGHT = 88;
const WRAP_CLASS_NAMES = new Set(['wrap-text', 'htWrap', 'wrap-cell']);
const SOFT_RECOLOR_PALETTE = Object.freeze([
  '#60a5fa', '#34d399', '#a78bfa', '#fbbf24', '#2dd4bf',
  '#818cf8', '#f472b6', '#38bdf8', '#a3e635', '#f59e0b',
  '#22c55e', '#06b6d4', '#c084fc', '#fb7185', '#84cc16',
  '#93c5fd', '#86efac', '#d8b4fe', '#fde68a', '#67e8f9'
]);

function createEmptyData(rows = 100, cols = 26) {
  return Array.from({ length: rows }, () => Array(cols).fill(''));
}

function classTokens(value) {
  return new Set(String(value || '').split(/\s+/).filter(Boolean));
}

function selectionContainsCell(selection, row, col) {
  if (!Array.isArray(selection?.[0]) || !Number.isInteger(row) || !Number.isInteger(col)) {
    return false;
  }

  return selection.some(([rawRow1, rawCol1, rawRow2, rawCol2]) => {
    if (![rawRow1, rawCol1, rawRow2, rawCol2].every(Number.isInteger)) {
      return false;
    }

    const rowStart = Math.min(rawRow1, rawRow2);
    const rowEnd = Math.max(rawRow1, rawRow2);
    const colStart = Math.min(rawCol1, rawCol2);
    const colEnd = Math.max(rawCol1, rawCol2);

    return row >= rowStart && row <= rowEnd && col >= colStart && col <= colEnd;
  });
}

export default {
  name: 'HermesSpreadsheetComponentAdvanced',
  emits: ['request-save', 'update-last-selection', 'deselected', 'ready', 'context-menu-cell'],
  props: {
    buttonLabel: { type: String, required: true },
    width: { type: [String, Number], default: '100%' },
    height: { type: [String, Number], default: '100%' },
    highlightHeader: { type: Boolean, default: false },
    freezeTopRow: { type: Boolean, default: false },
    autoSaveEnabled: { type: Boolean, default: true },
    stretchColumns: { type: String, default: 'last' },
    showAddRowsButton: { type: Boolean, default: true },
    enableFormulas: { type: Boolean, default: false },
    keepSelectionSelectors: {
      type: Array,
      default: () => ['.sheet-toolbar', '.modal-overlay', '.htMenu', '.htBorders']
    },
    customContextMenuItems: {
      type: [Object, Array, Function],
      default: null
    }
  },
  data() {
    return {
      hotInstance: null,
      cellClassMap: new Map(),
      lastSelection: null,
      lastContextMenuCell: null,
      resizeObserver: null,
      pasteHandler: null,
      suppressRowDisplaySave: false,
      contextMenuResolutionCache: { signature: '', values: {} },
      dynamicStyleTagId: `sheet-dynamic-styles-${Math.random().toString(36).slice(2, 10)}`
    };
  },
  mounted() {
    const vm = this;

    this.hotInstance = new Handsontable(this.$refs.spreadsheetContainer, {
      data: createEmptyData(100, 26),
      rowHeaders: true,
      colHeaders: true,
      minRows: 100,
      minCols: 26,
      colWidths: DEFAULT_COL_WIDTH,
      rowHeights: DEFAULT_ROW_HEIGHT,
      autoRowSize: false,
      wordWrap: false,
      width: this.width,
      height: this.height,
      stretchH: this.stretchColumns,
      manualRowResize: true,
      manualColumnResize: true,
      manualColumnMove: true,
      manualRowMove: true,
      customBorders: true,
      fixedRowsTop: this.freezeTopRow ? 1 : 0,
      search: true,
      columnSorting: {
        indicator: true,
        sortEmptyCells: false
      },
      selectionMode: 'multiple',
      formulas: this.enableFormulas ? {
        engine: HyperFormula
      } : undefined,
      licenseKey: 'non-commercial-and-evaluation',

      outsideClickDeselects(target) {
        return vm.shouldClearSelection(target);
      },

      afterSelectionEnd() {
        vm.clearContextMenuResolutionCache();
        const selected = vm.hotInstance?.getSelected();
        vm.lastSelection = Array.isArray(selected)
          ? selected.map(coords => coords.slice(0, 4))
          : null;

        vm.$emit('update-last-selection', vm.lastSelection);
      },

      afterDeselect() {
        vm.clearContextMenuResolutionCache();
        vm.lastSelection = null;
        vm.$emit('deselected');
      },

      afterOnCellMouseDown(event, coords) {
        if (event?.button !== 2 || !coords || coords.row < 0 || coords.col < 0) {
          return;
        }

        vm.clearContextMenuResolutionCache();
        const selected = vm.hotInstance?.getSelected();
        const clickedInsideSelection = vm.selectionContainsCell(selected, coords.row, coords.col);

        if (!clickedInsideSelection) {
          vm.hotInstance?.selectCell(coords.row, coords.col);
        }

        const nextSelected = vm.hotInstance?.getSelected();
        vm.lastSelection = Array.isArray(nextSelected)
          ? nextSelected.map(range => range.slice(0, 4))
          : [[coords.row, coords.col, coords.row, coords.col]];
        vm.$emit('update-last-selection', vm.lastSelection);
      },

      afterChange(changes, source) {
        if (!vm.autoSaveEnabled || !changes || source === 'loadData') {
          return;
        }

        vm.$emit('request-save');
      },

      afterColumnResize() {
        if (vm.autoSaveEnabled) {
          vm.$emit('request-save');
        }
      },

      afterRowResize() {
        if (vm.autoSaveEnabled && !vm.suppressRowDisplaySave) {
          vm.$emit('request-save');
        }
      },

      afterColumnMove() {
        if (vm.autoSaveEnabled) {
          vm.$emit('request-save');
        }
      },

      afterRowMove() {
        if (vm.autoSaveEnabled) {
          vm.$emit('request-save');
        }
      },

      afterCreateRow() {
        if (vm.autoSaveEnabled) {
          vm.$emit('request-save');
        }
      },

      afterRemoveRow() {
        if (vm.autoSaveEnabled) {
          vm.$emit('request-save');
        }
      },

      afterCreateCol() {
        if (vm.autoSaveEnabled) {
          vm.$emit('request-save');
        }
      },

      afterRemoveCol() {
        if (vm.autoSaveEnabled) {
          vm.$emit('request-save');
        }
      },

      contextMenu: vm.buildContextMenuSettings(),

      beforeOnCellContextMenu(event, coords) {
        vm.captureContextMenuCell(coords);
      },

      cells(row, col) {
        return vm.buildCellMeta(row, col, this);
      }
    });

    this.pasteHandler = (event) => this.onPaste(event);
    this.$refs.spreadsheetContainer.addEventListener('paste', this.pasteHandler);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.refreshDimensions();
      });
      this.resizeObserver.observe(this.$el);
    }

    this.restoreFormatting();
    this.$emit('ready', this.hotInstance);

    this.$nextTick(() => {
      this.refreshDimensions();
    });
  },
  beforeUnmount() {
    this.resizeObserver?.disconnect();

    if (this.pasteHandler && this.$refs.spreadsheetContainer) {
      this.$refs.spreadsheetContainer.removeEventListener('paste', this.pasteHandler);
    }

    const styleTag = document.getElementById(this.dynamicStyleTagId);
    styleTag?.remove();

    if (this.hotInstance) {
      this.hotInstance.destroy();
    }
  },
  watch: {
    width() {
      this.refreshDimensions(true);
    },
    height() {
      this.refreshDimensions(true);
    },
    highlightHeader() {
      this.hotInstance?.render();
    },
    stretchColumns(value) {
      if (this.hotInstance) {
        this.hotInstance.updateSettings({ stretchH: value });
        this.refreshDimensions();
      }
    },
    customContextMenuItems() {
      this.refreshContextMenuSettings();
    }
  },
  methods: {
    clearContextMenuResolutionCache() {
      this.contextMenuResolutionCache = { signature: '', values: {} };
    },

    selectionContainsCell(selection, targetRow, targetCol) {
      return this.normalizeSelection(selection).some(([rawRow1, rawCol1, rawRow2, rawCol2]) => {
        const rowStart = Math.max(0, Math.min(rawRow1, rawRow2));
        const rowEnd = Math.max(0, Math.max(rawRow1, rawRow2));
        const colStart = Math.max(0, Math.min(rawCol1, rawCol2));
        const colEnd = Math.max(0, Math.max(rawCol1, rawCol2));
        return targetRow >= rowStart && targetRow <= rowEnd && targetCol >= colStart && targetCol <= colEnd;
      });
    },

    getDefaultContextMenuItems() {
      const vm = this;
      return {
        ...Handsontable.plugins.ContextMenu.DEFAULT_ITEMS,
        separator_fill: Handsontable.plugins.ContextMenu.SEPARATOR,
        fill_down_custom: {
          name: '🠗 Fill Down Selection',
          callback() {
            fillDownSelection(vm.hotInstance);
          }
        },
        recolor_like_cells: {
          name: '🎨 Recolor Like Cells',
          callback() {
            vm.runUndoableFullClassChange('recolor_like_cells', () => {
              vm.recolorLikeCells({ clearExisting: true });
            });
          }
        }
      };
    },

    getContextMenuPayload() {
      const selection = Array.isArray(this.hotInstance?.getSelected?.())
        ? this.hotInstance.getSelected().map(coords => coords.slice(0, 4))
        : this.lastSelection;

      return {
        cell: this.lastContextMenuCell ? { ...this.lastContextMenuCell } : null,
        selection: Array.isArray(selection?.[0]) ? selection.map(coords => coords.slice(0, 4)) : null,
        hotInstance: this.hotInstance,
        sheet: this
      };
    },

    resolveCustomContextMenuItems() {
      const rawItems = typeof this.customContextMenuItems === 'function'
        ? this.customContextMenuItems(this.getContextMenuPayload())
        : this.customContextMenuItems;

      if (!rawItems) {
        return {};
      }

      const normalizeItem = (item) => {
        if (item === 'separator' || item?.separator === true) {
          return Handsontable.plugins.ContextMenu.SEPARATOR;
        }
        return item?.config || item;
      };

      if (Array.isArray(rawItems)) {
        return rawItems.reduce((items, item, index) => {
          if (!item) return items;
          if (item === 'separator' || item?.separator === true) {
            items[`custom_separator_${index}`] = Handsontable.plugins.ContextMenu.SEPARATOR;
          } else if (item.key) {
            items[item.key] = normalizeItem(item);
          } else {
            items[`custom_context_${index}`] = normalizeItem(item);
          }
          return items;
        }, {});
      }

      if (rawItems && typeof rawItems === 'object') {
        return Object.entries(rawItems).reduce((items, [key, item]) => {
          if (!item) return items;
          items[key] = normalizeItem(item);
          return items;
        }, {});
      }

      return {};
    },


    buildContextMenuSettings() {
      return {
        items: {
          ...this.getDefaultContextMenuItems(),
          ...this.resolveCustomContextMenuItems()
        }
      };
    },

    refreshContextMenuSettings() {
      if (!this.hotInstance) return;
      this.hotInstance.updateSettings({
        contextMenu: this.buildContextMenuSettings()
      });
    },

    captureContextMenuCell(coords = null) {
      const row = Number.isInteger(coords?.row) ? coords.row : -1;
      const col = Number.isInteger(coords?.col) ? coords.col : -1;
      this.lastContextMenuCell = row >= 0 && col >= 0 ? { row, col } : null;

      let selected = this.hotInstance?.getSelected?.();
      const normalizedSelection = Array.isArray(selected)
        ? selected.map(range => range.slice(0, 4))
        : this.lastSelection;

      if (row >= 0 && col >= 0 && !selectionContainsCell(normalizedSelection, row, col)) {
        if (typeof this.hotInstance?.selectCells === 'function') {
          this.hotInstance.selectCells([[row, col, row, col]], false, false);
        } else {
          this.hotInstance?.selectCell?.(row, col);
        }
        selected = this.hotInstance?.getSelected?.() || [[row, col, row, col]];
      }

      this.lastSelection = Array.isArray(selected)
        ? selected.map(range => range.slice(0, 4))
        : normalizedSelection;

      this.clearContextMenuResolutionCache();
      this.$emit('update-last-selection', this.lastSelection);
      this.$emit('context-menu-cell', this.getContextMenuPayload());

      if (this.customContextMenuItems) {
        this.refreshContextMenuSettings();
      }
    },


    getRangeData(startRow, startCol, endRow, endCol) {
  const hot = this.hotInstance;
  if (!hot) return [];

  const rowStart = Math.max(0, Math.min(startRow, endRow));
  const rowEnd = Math.max(0, Math.max(startRow, endRow));
  const colStart = Math.max(0, Math.min(startCol, endCol));
  const colEnd = Math.max(0, Math.max(startCol, endCol));

  const values = [];
  for (let row = rowStart; row <= rowEnd; row++) {
    const rowValues = [];
    for (let col = colStart; col <= colEnd; col++) {
      rowValues.push(hot.getDataAtCell(row, col) ?? '');
    }
    values.push(rowValues);
  }

  return values;
},

setRangeInput(startRow, startCol, matrix, source = 'agent_panel') {
  const hot = this.hotInstance;

  if (
    !hot ||
    !Number.isInteger(startRow) ||
    !Number.isInteger(startCol) ||
    startRow < 0 ||
    startCol < 0 ||
    !Array.isArray(matrix) ||
    matrix.length === 0
  ) {
    return { ok: false, status: 'Invalid range input.' };
  }

  const normalizedMatrix = matrix.map(row => {
    if (Array.isArray(row)) {
      return row.map(value => value == null ? '' : value);
    }
    return [row == null ? '' : row];
  });

  let maxCols = 0;
  const changes = [];

  normalizedMatrix.forEach((rowValues, rowOffset) => {
    maxCols = Math.max(maxCols, rowValues.length);
    rowValues.forEach((value, colOffset) => {
      changes.push([startRow + rowOffset, startCol + colOffset, value]);
    });
  });

  if (!changes.length || maxCols === 0) {
    return { ok: false, status: 'No range values supplied.' };
  }

  hot.setDataAtCell(changes, source);
  hot.selectCell(startRow, startCol);
  hot.listen?.();

  return {
    ok: true,
    status: 'Range written successfully.',
    rowCount: normalizedMatrix.length,
    colCount: maxCols,
    endRow: startRow + normalizedMatrix.length - 1,
    endCol: startCol + maxCols - 1
  };
},
    getRowsFromSelection(selection = this.lastSelection) {
      const hot = this.hotInstance;
      if (!hot) return [];

      const rowCount = hot.countRows();
      const rows = new Set();

      this.normalizeSelection(selection).forEach(([rawStartRow, , rawEndRow]) => {
        const rowStart = Math.max(0, Math.min(rawStartRow, rawEndRow));
        const rowEnd = Math.min(rowCount - 1, Math.max(rawStartRow, rawEndRow));

        if (rowEnd < rowStart) {
          return;
        }

        for (let row = rowStart; row <= rowEnd; row += 1) {
          rows.add(row);
        }
      });

      return [...rows].sort((a, b) => a - b);
    },

    normalizeRowList(rowsOrSelection = this.lastSelection) {
      const hot = this.hotInstance;
      if (!hot) return [];

      const sourceRows = Array.isArray(rowsOrSelection) && rowsOrSelection.every(Number.isInteger)
        ? rowsOrSelection
        : this.getRowsFromSelection(rowsOrSelection);

      return [...new Set(sourceRows)]
        .filter(row => Number.isInteger(row) && row >= 0 && row < hot.countRows())
        .sort((a, b) => a - b);
    },

    captureRowsHeightState(rowsOrSelection = this.lastSelection) {
      const hot = this.hotInstance;
      if (!hot) return [];

      return this.normalizeRowList(rowsOrSelection).map(row => ({
        row,
        height: hot.getRowHeight(row) || DEFAULT_ROW_HEIGHT
      }));
    },

    applyRowsHeightState(snapshot = []) {
      const hot = this.hotInstance;
      if (!hot || !Array.isArray(snapshot)) return false;

      this.suppressRowDisplaySave = true;

      try {
        snapshot.forEach(({ row, height }) => {
          this.setManualRowHeight(row, height || DEFAULT_ROW_HEIGHT);
        });
      } finally {
        this.suppressRowDisplaySave = false;
      }

      hot.render();
      return true;
    },

    cellHasWrappedText(row, col) {
      const hot = this.hotInstance;
      if (!hot || !Number.isInteger(row) || !Number.isInteger(col)) return false;

      const key = `${row},${col}`;
      const meta = hot.getCellMeta?.(row, col) || {};
      const classes = classTokens(`${this.cellClassMap.get(key) || ''} ${meta.className || ''}`);

      if (classes.has('force-nowrap')) {
        return false;
      }

      for (const wrapClassName of WRAP_CLASS_NAMES) {
        if (classes.has(wrapClassName)) {
          return true;
        }
      }

      return false;
    },

    rowHasWrappedText(row) {
      const hot = this.hotInstance;
      if (!hot || !Number.isInteger(row) || row < 0 || row >= hot.countRows()) {
        return false;
      }

      const colCount = hot.countCols();

      for (let col = 0; col < colCount; col += 1) {
        if (this.cellHasWrappedText(row, col)) {
          return true;
        }
      }

      return false;
    },

    setManualRowHeight(row, height) {
      const hot = this.hotInstance;
      const nextHeight = Math.max(
        DEFAULT_ROW_HEIGHT,
        Number.isFinite(Number(height)) ? Number(height) : DEFAULT_ROW_HEIGHT
      );

      if (!hot || !Number.isInteger(row) || row < 0 || row >= hot.countRows()) {
        return false;
      }

      const manualRowResizePlugin = hot.getPlugin?.('manualRowResize');

      if (manualRowResizePlugin?.setManualSize) {
        try {
          manualRowResizePlugin.setManualSize(row, nextHeight);
          return true;
        } catch (error) {
          console.warn('Failed to set manual row height through ManualRowResize. Falling back to settings update.', error);
        }
      }

      const rowHeights = Array.from(
        { length: hot.countRows() },
        (_, rowIndex) => hot.getRowHeight(rowIndex) || DEFAULT_ROW_HEIGHT
      );

      rowHeights[row] = nextHeight;
      hot.updateSettings({ manualRowResize: rowHeights });

      return true;
    },

    setAllManualRowHeights(height = DEFAULT_ROW_HEIGHT) {
      const hot = this.hotInstance;
      if (!hot) return false;

      const nextHeight = Math.max(
        DEFAULT_ROW_HEIGHT,
        Number.isFinite(Number(height)) ? Number(height) : DEFAULT_ROW_HEIGHT
      );
      const rowCount = hot.countRows();
      const manualRowResizePlugin = hot.getPlugin?.('manualRowResize');

      if (manualRowResizePlugin?.setManualSize) {
        try {
          for (let row = 0; row < rowCount; row += 1) {
            manualRowResizePlugin.setManualSize(row, nextHeight);
          }
          return true;
        } catch (error) {
          console.warn('Failed to reset manual row heights through ManualRowResize. Falling back to settings update.', error);
        }
      }

      hot.updateSettings({
        manualRowResize: Array.from({ length: rowCount }, () => nextHeight)
      });
      return true;
    },

    syncWrappedRowHeights(selection = this.lastSelection, { emitSave = false } = {}) {
      const hot = this.hotInstance;
      if (!hot) return false;

      const rows = this.normalizeRowList(selection);
      if (rows.length === 0) return false;

      this.suppressRowDisplaySave = true;

      try {
        rows.forEach((row) => {
          const targetHeight = this.rowHasWrappedText(row)
            ? WRAPPED_ROW_HEIGHT
            : DEFAULT_ROW_HEIGHT;

          this.setManualRowHeight(row, targetHeight);
        });
      } finally {
        this.suppressRowDisplaySave = false;
      }

      hot.render();

      if (emitSave && this.autoSaveEnabled) {
        this.$emit('request-save');
      }

      return true;
    },

    removeWrapClassesFromCell(row, col) {
      const hot = this.hotInstance;
      if (!hot || !Number.isInteger(row) || !Number.isInteger(col) || row < 0 || col < 0) {
        return false;
      }

      const key = `${row},${col}`;
      const remainingClasses = String(this.cellClassMap.get(key) || '')
        .split(/\s+/)
        .filter(Boolean)
        .filter(token => !WRAP_CLASS_NAMES.has(token))
        .join(' ');

      hot.removeCellMeta(row, col, 'wordWrap');

      if (remainingClasses) {
        this.cellClassMap.set(key, remainingClasses);
        hot.setCellMeta(row, col, 'className', remainingClasses);
      } else {
        this.cellClassMap.delete(key);
        hot.removeCellMeta(row, col, 'className');
      }

      return true;
    },

    clearStaleWordWrapMeta() {
      const hot = this.hotInstance;
      if (!hot || typeof hot.getCellsMeta !== 'function') return;

      hot.getCellsMeta().forEach((meta) => {
        if (!Number.isInteger(meta?.row) || !Number.isInteger(meta?.col)) {
          return;
        }

        hot.removeCellMeta(meta.row, meta.col, 'wordWrap');

        const metaClasses = classTokens(meta.className);
        let changed = false;

        for (const wrapClassName of WRAP_CLASS_NAMES) {
          if (metaClasses.delete(wrapClassName)) {
            changed = true;
          }
        }

        if (!changed) {
          return;
        }

        const remainingClassName = [...metaClasses].join(' ');
        if (remainingClassName) {
          hot.setCellMeta(meta.row, meta.col, 'className', remainingClassName);
        } else {
          hot.removeCellMeta(meta.row, meta.col, 'className');
        }
      });
    },

    resetRowDisplayToDefault() {
      const hot = this.hotInstance;
      if (!hot) return false;

      const classEntries = Array.from(this.cellClassMap.keys());
      this.suppressRowDisplaySave = true;

      try {
        classEntries.forEach((key) => {
          const [row, col] = String(key).split(',').map(Number);
          this.removeWrapClassesFromCell(row, col);
        });

        this.clearStaleWordWrapMeta();

        hot.updateSettings({
          rowHeights: DEFAULT_ROW_HEIGHT,
          autoRowSize: false,
          wordWrap: false,
          manualRowResize: true
        });

        this.setAllManualRowHeights(DEFAULT_ROW_HEIGHT);
      } finally {
        this.suppressRowDisplaySave = false;
      }

      this.restoreFormatting();
      hot.render();

      if (this.autoSaveEnabled) {
        this.$emit('request-save');
      }

      return true;
    },

    resetRowHeightsToDefault() {
      return this.resetRowDisplayToDefault();
    },


    buildCellMeta(row, col, cellProperties) {
      const key = `${row},${col}`;
      const mergedClasses = classTokens(this.cellClassMap.get(key));

      if (cellProperties && typeof cellProperties.className === 'string') {
        cellProperties.className
          .split(/\s+/)
          .filter(Boolean)
          .forEach(token => mergedClasses.add(token));
      }

      if (this.highlightHeader && row === 0) {
        mergedClasses.add('header-highlight');
      }

      const shouldWrap =
        !mergedClasses.has('force-nowrap') &&
        [...WRAP_CLASS_NAMES].some(className => mergedClasses.has(className));

      return {
        className: [...mergedClasses].join(' '),
        wordWrap: shouldWrap
      };
    },

    ensureDynamicStyleTag() {
      let styleTag = document.getElementById(this.dynamicStyleTagId);

      if (!styleTag) {
        styleTag = document.createElement('style');
        styleTag.id = this.dynamicStyleTagId;
        document.head.appendChild(styleTag);
      }

      return styleTag;
    },

    buildDynamicStyles() {
      const seen = new Set();
      let css = '';

      for (const classList of this.cellClassMap.values()) {
        const tokens = String(classList || '').split(/\s+/).filter(Boolean);

        for (const className of tokens) {
          if (seen.has(className)) continue;
          seen.add(className);

          if (className.startsWith('color-')) {
            const hex = `#${className.slice(6)}`;
            css += `td.${className} { background-color: ${hex} !important; }\n`;
          } else if (className.startsWith('text-')) {
            const hex = `#${className.slice(5)}`;
            css += `td.${className} { color: ${hex} !important; }\n`;
          } else if (/^case-address-color-[0-9a-f]{6}$/i.test(className)) {
            const hex = `#${className.slice('case-address-color-'.length)}`;
            css += `td.${className} { background-color: ${hex} !important; }\n`;
          } else if (/^case-address-text-[0-9a-f]{6}$/i.test(className)) {
            const hex = `#${className.slice('case-address-text-'.length)}`;
            css += `td.${className} { color: ${hex} !important; }\n`;
          } else if (className === 'bold-cell') {
            css += 'td.bold-cell { font-weight: bold !important; }\n';
          } else if (className === 'italic-cell') {
            css += 'td.italic-cell { font-style: italic !important; }\n';
          } else if (className === 'underline-cell') {
            css += 'td.underline-cell { text-decoration: underline !important; }\n';
          } else if (className === 'strike-cell') {
            css += 'td.strike-cell { text-decoration: line-through !important; }\n';
          } else if (className === 'align-left') {
            css += 'td.align-left { text-align: left !important; }\n';
          } else if (className === 'align-center') {
            css += 'td.align-center { text-align: center !important; }\n';
          } else if (className === 'align-right') {
            css += 'td.align-right { text-align: right !important; }\n';
          } else if (className === 'valign-top') {
            css += 'td.valign-top { vertical-align: top !important; }\n';
          } else if (className === 'valign-middle') {
            css += 'td.valign-middle { vertical-align: middle !important; }\n';
          } else if (className === 'valign-bottom') {
            css += 'td.valign-bottom { vertical-align: bottom !important; }\n';
          } else if (className === 'wrap-text' || className === 'htWrap' || className === 'wrap-cell') {
            css += `td.${className} { white-space: normal !important; word-break: break-word !important; overflow: hidden; text-overflow: clip; line-height: 1.2; }\n`;
          } else if (className === 'force-nowrap') {
            css += 'td.force-nowrap { white-space: nowrap !important; overflow: hidden; text-overflow: ellipsis; }\n';
          } else if (/^font-\d+$/.test(className)) {
            const size = className.split('-')[1];
            css += `td.${className} { font-size: ${size}px !important; }\n`;
          }
        }
      }

      return css;
    },

    restoreFormatting() {
      const styleTag = this.ensureDynamicStyleTag();
      styleTag.textContent = this.buildDynamicStyles();
      this.hotInstance?.render();
    },

    clearRecolorClassesForCoords(coords = []) {
      const normalizedCoords = Array.isArray(coords) ? coords : [];
      let changed = false;

      normalizedCoords.forEach(({ row, col }) => {
        if (!Number.isInteger(row) || !Number.isInteger(col) || row < 0 || col < 0) {
          return;
        }

        const key = `${row},${col}`;
        const remaining = String(this.cellClassMap.get(key) || '')
          .split(/\s+/)
          .filter(Boolean)
          .filter(token => !token.startsWith('color-') && !token.startsWith('text-') && !token.startsWith('case-address-color-') && !token.startsWith('case-address-coloring-') && !token.startsWith('case-address-text-'));

        const nextClassName = remaining.join(' ');
        const previousClassName = this.cellClassMap.get(key) || '';

        if (nextClassName) {
          this.cellClassMap.set(key, nextClassName);
          this.hotInstance?.setCellMeta(row, col, 'className', nextClassName);
        } else {
          this.cellClassMap.delete(key);
          this.hotInstance?.removeCellMeta(row, col, 'className');
        }

        if (previousClassName !== nextClassName) {
          changed = true;
        }
      });

      return changed;
    },

    clearRecolorsForSelection(selection = this.lastSelection, options = {}) {
      const coords = this.selectionToCoords(selection);
      const changed = this.clearRecolorClassesForCoords(coords);

      if (options.render !== false) {
        this.restoreFormatting();
      }

      return { changed, clearedCellCount: coords.length };
    },

    coordsForRange(startRow, startCol, endRow, endCol) {
      const hot = this.hotInstance;
      if (!hot) return [];

      const rowStart = Math.max(0, Math.min(Number(startRow), Number(endRow)));
      const rowEnd = Math.min(hot.countRows() - 1, Math.max(Number(startRow), Number(endRow)));
      const colStart = Math.max(0, Math.min(Number(startCol), Number(endCol)));
      const colEnd = Math.min(hot.countCols() - 1, Math.max(Number(startCol), Number(endCol)));

      if (![rowStart, rowEnd, colStart, colEnd].every(Number.isFinite) || rowEnd < rowStart || colEnd < colStart) {
        return [];
      }

      const coords = [];
      for (let row = rowStart; row <= rowEnd; row += 1) {
        for (let col = colStart; col <= colEnd; col += 1) {
          coords.push({ row, col });
        }
      }
      return coords;
    },

    clearRecolorsForRange(startRow, startCol, endRow, endCol, options = {}) {
      const coords = this.coordsForRange(startRow, startCol, endRow, endCol);
      const changed = this.clearRecolorClassesForCoords(coords);

      if (options.render !== false) {
        this.restoreFormatting();
      }

      if (changed && options.emitSave !== false && this.autoSaveEnabled) {
        this.$emit('request-save');
      }

      return { changed, clearedCellCount: coords.length };
    },

    clearRecolors() {
      for (const [key, className] of Array.from(this.cellClassMap.entries())) {
        const [row, col] = String(key).split(',').map(Number);
        const remaining = String(className || '')
          .split(/\s+/)
          .filter(Boolean)
          .filter(token => !token.startsWith('color-') && !token.startsWith('text-') && !token.startsWith('case-address-color-') && !token.startsWith('case-address-coloring-') && !token.startsWith('case-address-text-'));

        if (remaining.length > 0) {
          const nextClassName = remaining.join(' ');
          this.cellClassMap.set(key, nextClassName);
          if (Number.isInteger(row) && Number.isInteger(col)) {
            this.hotInstance?.setCellMeta(row, col, 'className', nextClassName);
          }
        } else {
          this.cellClassMap.delete(key);
          if (Number.isInteger(row) && Number.isInteger(col)) {
            this.hotInstance?.removeCellMeta(row, col, 'className');
          }
        }
      }

      this.restoreFormatting();
    },


    shouldClearSelection(target) {
      if (!(target instanceof Element)) {
        return true;
      }

      return !this.keepSelectionSelectors.some(selector => target.closest(selector));
    },

    refreshDimensions(forceSettingsUpdate = false) {
      if (!this.hotInstance) return;

      if (forceSettingsUpdate) {
        this.hotInstance.updateSettings({
          width: this.width,
          height: this.height
        });
      }

      if (typeof this.hotInstance.refreshDimensions === 'function') {
        this.hotInstance.refreshDimensions();
      } else {
        this.hotInstance.render();
      }
    },

    getSheetState() {
      return captureSpreadsheetState(this);
    },

    applySheetState(state) {
      try {
        const applied = applySpreadsheetState(this, state);
        this.clearUndoRedoHistory();
        return applied;
      } catch (error) {
        console.error('Failed to apply saved sheet state. Resetting to a safe blank sheet instead.', error, state);
        this.resetSheet();
        this.clearUndoRedoHistory();
        return null;
      }
    },

    clearUndoRedoHistory() {
      this.getUndoRedoPlugin()?.clear?.();
    },

    getFormulasPlugin() {
      if (!this.enableFormulas || !this.hotInstance) {
        return null;
      }

      const plugin = this.hotInstance.getPlugin?.('formulas');
      if (!plugin || (typeof plugin.isEnabled === 'function' && !plugin.isEnabled())) {
        return null;
      }

      return plugin;
    },

    getFormulaEngine() {
      return this.getFormulasPlugin()?.engine || null;
    },

    getFormulaSheetId() {
      const sheetId = this.getFormulasPlugin()?.sheetId;
      return Number.isInteger(sheetId) ? sheetId : null;
    },

    getPersistableData() {
      const engine = this.getFormulaEngine();
      const sheetId = this.getFormulaSheetId();

      if (engine && Number.isInteger(sheetId) && typeof engine.getSheetSerialized === 'function') {
        try {
          return engine.getSheetSerialized(sheetId);
        } catch (error) {
          console.warn('Failed to serialize formula sheet state; falling back to grid data.', error);
        }
      }

      return this.hotInstance?.getData() || [];
    },

    getComputedData() {
      const engine = this.getFormulaEngine();
      const sheetId = this.getFormulaSheetId();

      if (engine && Number.isInteger(sheetId) && typeof engine.getSheetValues === 'function') {
        try {
          return engine.getSheetValues(sheetId);
        } catch (error) {
          console.warn('Failed to read computed formula values; falling back to grid data.', error);
        }
      }

      return this.hotInstance?.getData() || [];
    },

    normalizeSelection(selection = this.lastSelection) {
      const normalized = Array.isArray(selection?.[0]) ? selection : [];

      return normalized
        .map(coords => coords.slice(0, 4))
        .filter(([r1, c1, r2, c2]) => {
          return [r1, c1, r2, c2].every(value => Number.isInteger(value));
        });
    },

    selectionToCoords(selection = this.lastSelection) {
      const ranges = this.normalizeSelection(selection);
      const unique = new Map();

      ranges.forEach(([rawRow1, rawCol1, rawRow2, rawCol2]) => {
        const startRow = Math.max(0, Math.min(rawRow1, rawRow2));
        const endRow = Math.max(0, Math.max(rawRow1, rawRow2));
        const startCol = Math.max(0, Math.min(rawCol1, rawCol2));
        const endCol = Math.max(0, Math.max(rawCol1, rawCol2));

        for (let row = startRow; row <= endRow; row += 1) {
          for (let col = startCol; col <= endCol; col += 1) {
            const key = `${row}:${col}`;
            if (!unique.has(key)) {
              unique.set(key, { row, col });
            }
          }
        }
      });

      return [...unique.values()].sort((a, b) => a.row - b.row || a.col - b.col);
    },

    captureCellsClassState(selection = this.lastSelection) {
      const coords = this.selectionToCoords(selection);

      return coords.map(({ row, col }) => ({
        row,
        col,
        className: this.cellClassMap.get(`${row},${col}`) || ''
      }));
    },

    applyCellsClassState(snapshot) {
      const hot = this.hotInstance;
      if (!hot || !Array.isArray(snapshot)) return;

      snapshot.forEach(({ row, col, className }) => {
        const key = `${row},${col}`;
        const classes = classTokens(className);
        const shouldWrap =
          !classes.has('force-nowrap') &&
          [...WRAP_CLASS_NAMES].some(wrapClassName => classes.has(wrapClassName));

        if (className) {
          this.cellClassMap.set(key, className);
          hot.setCellMeta(row, col, 'className', className);
        } else {
          this.cellClassMap.delete(key);
          hot.removeCellMeta(row, col, 'className');
        }

        if (shouldWrap) {
          hot.setCellMeta(row, col, 'wordWrap', true);
        } else {
          hot.removeCellMeta(row, col, 'wordWrap');
        }
      });

      this.restoreFormatting();
    },

    restoreSelection(selection = this.lastSelection) {
      const hot = this.hotInstance;
      const ranges = this.normalizeSelection(selection);

      if (!hot || ranges.length === 0) return;

      hot.selectCells(ranges);
      this.lastSelection = ranges.map(coords => coords.slice(0, 4));
      this.$emit('update-last-selection', this.lastSelection);
    },

    captureFullClassState() {
      return [...this.cellClassMap.entries()].sort((a, b) => String(a[0]).localeCompare(String(b[0])));
    },

    applyFullClassState(snapshot) {
      const hot = this.hotInstance;
      if (!hot || !Array.isArray(snapshot)) return;

      const currentKeys = new Set(this.cellClassMap.keys());
      const nextMap = new Map(snapshot);
      const nextKeys = new Set(nextMap.keys());
      const allKeys = new Set([...currentKeys, ...nextKeys]);

      allKeys.forEach((key) => {
        const [row, col] = key.split(',').map(Number);
        const className = nextMap.get(key) || '';
        const classes = classTokens(className);
        const shouldWrap =
          !classes.has('force-nowrap') &&
          [...WRAP_CLASS_NAMES].some(wrapClassName => classes.has(wrapClassName));

        if (className) {
          hot.setCellMeta(row, col, 'className', className);
        } else {
          hot.removeCellMeta(row, col, 'className');
        }

        if (shouldWrap) {
          hot.setCellMeta(row, col, 'wordWrap', true);
        } else {
          hot.removeCellMeta(row, col, 'wordWrap');
        }
      });

      this.cellClassMap = nextMap;
      this.restoreFormatting();
    },

    captureCellsBorderState(selection = this.lastSelection) {
      const hot = this.hotInstance;
      const plugin = hot?.getPlugin('customBorders');
      const coords = this.selectionToCoords(selection);

      const borderData = typeof plugin?.getBorders === 'function' ? plugin.getBorders() : [];
      const borderMap = new Map(
        borderData.map((border) => [
          `${border.row}:${border.col}`,
          {
            row: border.row,
            col: border.col,
            ...(border.top ? { top: { ...border.top } } : {}),
            ...(border.right ? { right: { ...border.right } } : {}),
            ...(border.bottom ? { bottom: { ...border.bottom } } : {}),
            ...(border.left ? { left: { ...border.left } } : {})
          }
        ])
      );

      return coords.map(({ row, col }) => {
        const current = borderMap.get(`${row}:${col}`);
        return current
          ? {
              row: current.row,
              col: current.col,
              ...(current.top ? { top: { ...current.top } } : {}),
              ...(current.right ? { right: { ...current.right } } : {}),
              ...(current.bottom ? { bottom: { ...current.bottom } } : {}),
              ...(current.left ? { left: { ...current.left } } : {})
            }
          : { row, col };
      });
    },

    applyCellsBorderState(snapshot) {
      const hot = this.hotInstance;
      const plugin = hot?.getPlugin('customBorders');
      if (!hot || !plugin || !Array.isArray(snapshot)) return;

      snapshot.forEach(({ row, col }) => {
        plugin.clearBorders([[row, col, row, col]]);
      });

      snapshot.forEach((border) => {
        const config = {};
        if (border.top) config.top = { ...border.top };
        if (border.right) config.right = { ...border.right };
        if (border.bottom) config.bottom = { ...border.bottom };
        if (border.left) config.left = { ...border.left };

        if (Object.keys(config).length > 0) {
          plugin.setBorders([[border.row, border.col]], config);
        }
      });

      hot.render();
    },

    getUndoRedoPlugin() {
      return this.hotInstance?.getPlugin('undoRedo') || this.hotInstance?.undoRedo || null;
    },

    registerCustomUndoRedoAction(action, source = 'custom') {
      const plugin = this.getUndoRedoPlugin();
      if (!plugin?.done || !action) return;

      plugin.done(() => action, source);
    },

    snapshotsEqual(before, after) {
      return JSON.stringify(before) === JSON.stringify(after);
    },

    runUndoableClassChange(label, selection, mutation) {
      if (typeof mutation !== 'function') return false;

      const selectionSnapshot = this.normalizeSelection(selection);
      const shouldSyncWrapHeights = String(label || '').toLowerCase().includes('wrap');
      const affectedRows = shouldSyncWrapHeights ? this.normalizeRowList(selectionSnapshot) : [];
      const before = this.captureCellsClassState(selectionSnapshot);
      const beforeRowHeights = shouldSyncWrapHeights
        ? this.captureRowsHeightState(affectedRows)
        : [];

      mutation();

      if (shouldSyncWrapHeights) {
        this.syncWrappedRowHeights(affectedRows);
      }

      const after = this.captureCellsClassState(selectionSnapshot);
      const afterRowHeights = shouldSyncWrapHeights
        ? this.captureRowsHeightState(affectedRows)
        : [];

      if (this.snapshotsEqual(before, after) && this.snapshotsEqual(beforeRowHeights, afterRowHeights)) {
        return false;
      }

      this.registerCustomUndoRedoAction({
        actionType: label,
        undo: (_hot, done) => {
          this.applyCellsClassState(before);

          if (shouldSyncWrapHeights) {
            this.applyRowsHeightState(beforeRowHeights);
          }

          this.restoreSelection(selectionSnapshot);

          if (this.autoSaveEnabled) {
            this.$emit('request-save');
          }

          done?.();
        },
        redo: (_hot, done) => {
          this.applyCellsClassState(after);

          if (shouldSyncWrapHeights) {
            this.applyRowsHeightState(afterRowHeights);
          }

          this.restoreSelection(selectionSnapshot);

          if (this.autoSaveEnabled) {
            this.$emit('request-save');
          }

          done?.();
        }
      }, label);

      if (this.autoSaveEnabled) {
        this.$emit('request-save');
      }

      return true;
    },

    runUndoableFullClassChange(label, mutation) {
      if (typeof mutation !== 'function') return false;

      const selectionSnapshot = this.normalizeSelection(this.lastSelection);
      const before = this.captureFullClassState();
      mutation();
      const after = this.captureFullClassState();

      if (this.snapshotsEqual(before, after)) {
        return false;
      }

      this.registerCustomUndoRedoAction({
        actionType: label,
        undo: (_hot, done) => {
          this.applyFullClassState(before);
          this.restoreSelection(selectionSnapshot);

          if (this.autoSaveEnabled) {
            this.$emit('request-save');
          }

          done?.();
        },
        redo: (_hot, done) => {
          this.applyFullClassState(after);
          this.restoreSelection(selectionSnapshot);

          if (this.autoSaveEnabled) {
            this.$emit('request-save');
          }

          done?.();
        }
      }, label);

      if (this.autoSaveEnabled) {
        this.$emit('request-save');
      }

      return true;
    },

    runUndoableBorderChange(label, selection, mutation) {
      if (typeof mutation !== 'function') return false;

      const selectionSnapshot = this.normalizeSelection(selection);
      const before = this.captureCellsBorderState(selection);
      mutation();
      const after = this.captureCellsBorderState(selection);

      if (this.snapshotsEqual(before, after)) {
        return false;
      }

      this.registerCustomUndoRedoAction({
        actionType: label,
        undo: (_hot, done) => {
          this.applyCellsBorderState(before);
          this.restoreSelection(selectionSnapshot);

          if (this.autoSaveEnabled) {
            this.$emit('request-save');
          }

          done?.();
        },
        redo: (_hot, done) => {
          this.applyCellsBorderState(after);
          this.restoreSelection(selectionSnapshot);

          if (this.autoSaveEnabled) {
            this.$emit('request-save');
          }

          done?.();
        }
      }, label);

      if (this.autoSaveEnabled) {
        this.$emit('request-save');
      }

      return true;
    },
    resetSheet(rows = 100, cols = 26) {
      const hot = this.hotInstance;
      if (!hot) {
        return { ok: false, status: 'Spreadsheet not ready.' };
      }

      const searchPlugin = hot.getPlugin('search');
      const bordersPlugin = hot.getPlugin('customBorders');

      this.cellClassMap.clear();
      this.lastSelection = null;

      if (searchPlugin?.query) {
        searchPlugin.query('');
      }

      this.suppressRowDisplaySave = true;

      try {
        hot.updateSettings({
          fixedRowsTop: 0,
          fixedColumnsLeft: 0,
          colWidths: DEFAULT_COL_WIDTH,
          rowHeights: DEFAULT_ROW_HEIGHT,
          autoRowSize: false,
          wordWrap: false,
          manualRowResize: true
        });

        hot.loadData(createEmptyData(rows, cols));
        this.setAllManualRowHeights(DEFAULT_ROW_HEIGHT);
      } finally {
        this.suppressRowDisplaySave = false;
      }

      bordersPlugin?.clearBorders?.();
      hot.deselectCell?.();

      this.restoreFormatting();
      this.clearUndoRedoHistory();
      this.$emit('update-last-selection', null);
      this.$emit('deselected');
      this.refreshDimensions();

      return {
        ok: true,
        status: 'Sheet reset.',
        rowCount: rows,
        colCount: cols,
        sample: this.getRangeData(0, 0, Math.min(rows - 1, 2), Math.min(cols - 1, 2))
      };
    },
    buildRecolorPalette(options = {}) {
      const customPalette = Array.isArray(options.palette) ? options.palette : [];
      const palette = (customPalette.length > 0 ? customPalette : SOFT_RECOLOR_PALETTE)
        .map(color => String(color || '').trim())
        .filter(color => /^#[0-9a-f]{6}$/i.test(color));

      if (options.shuffle === false) {
        return palette.slice();
      }

      return palette
        .map(color => ({ color, sort: Math.random() }))
        .sort((a, b) => a.sort - b.sort)
        .map(item => item.color);
    },

    nextFallbackRecolor(usedColors) {
      let color = '#000000';
      let attempts = 0;

      do {
        const hue = Math.floor(Math.random() * 360);
        const saturation = 58 + Math.floor(Math.random() * 18);
        const lightness = 58 + Math.floor(Math.random() * 14);
        color = this.hslToHex(hue, saturation, lightness);
        attempts += 1;
      } while (usedColors.has(color) && attempts < 80);

      return color;
    },

    hslToHex(hue, saturation, lightness) {
      const s = saturation / 100;
      const l = lightness / 100;
      const c = (1 - Math.abs(2 * l - 1)) * s;
      const x = c * (1 - Math.abs((hue / 60) % 2 - 1));
      const m = l - c / 2;
      let r = 0;
      let g = 0;
      let b = 0;

      if (hue < 60) { [r, g, b] = [c, x, 0]; }
      else if (hue < 120) { [r, g, b] = [x, c, 0]; }
      else if (hue < 180) { [r, g, b] = [0, c, x]; }
      else if (hue < 240) { [r, g, b] = [0, x, c]; }
      else if (hue < 300) { [r, g, b] = [x, 0, c]; }
      else { [r, g, b] = [c, 0, x]; }

      const toHex = value => Math.round((value + m) * 255).toString(16).padStart(2, '0');
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    },

    recolorCoordsLikeCells(coords = [], options = {}) {
      const hot = this.hotInstance;
      const selectedCoords = Array.isArray(coords)
        ? coords.filter(({ row, col }) => Number.isInteger(row) && Number.isInteger(col) && row >= 0 && col >= 0)
        : [];

      if (!hot || selectedCoords.length === 0) {
        return { ok: false, status: 'No valid selected cells.', groupedValueCount: 0, coloredCellCount: 0, selectedCellCount: 0 };
      }

      if (options.clearExisting !== false) {
        this.clearRecolorClassesForCoords(selectedCoords);
      }

      const groupedValues = new Map();

      selectedCoords.forEach(({ row, col }) => {
        const value = hot.getDataAtCell(row, col)?.toString().trim();
        if (!value) return;

        if (!groupedValues.has(value)) {
          groupedValues.set(value, []);
        }

        groupedValues.get(value).push([row, col]);
      });

      const minGroupSize = Math.max(1, Number(options.minGroupSize || 2));
      const usedColors = new Set();
      const palette = this.buildRecolorPalette(options);
      let paletteIndex = 0;
      const nextColor = () => {
        let color = palette[paletteIndex];
        paletteIndex += 1;

        if (!color || usedColors.has(color)) {
          color = this.nextFallbackRecolor(usedColors);
        }

        usedColors.add(color);
        return color;
      };

      let coloredCellCount = 0;
      let groupedValueCount = 0;

      groupedValues.forEach((positions) => {
        if (positions.length < minGroupSize) return;

        groupedValueCount += 1;
        const background = nextColor();
        const text = this.isDarkColor(background) ? '#ffffff' : '#000000';
        const backgroundClass = `color-${background.slice(1)}`;
        const textClass = `text-${text.slice(1)}`;

        positions.forEach(([row, col]) => {
          const key = `${row},${col}`;
          const tokens = classTokens(this.cellClassMap.get(key));

          [...tokens].forEach(token => {
            if (token.startsWith('color-') || token.startsWith('text-') || token.startsWith('case-address-color-') || token.startsWith('case-address-coloring-') || token.startsWith('case-address-text-')) {
              tokens.delete(token);
            }
          });

          tokens.add(backgroundClass);
          tokens.add(textClass);

          const className = [...tokens].join(' ');
          this.cellClassMap.set(key, className);
          hot.setCellMeta(row, col, 'className', className);
          coloredCellCount += 1;
        });
      });

      this.restoreFormatting();

      if (options.emitSave !== false && this.autoSaveEnabled) {
        this.$emit('request-save');
      }

      return {
        ok: true,
        status: groupedValueCount > 0
          ? `Colored ${coloredCellCount} cell${coloredCellCount === 1 ? '' : 's'} across ${groupedValueCount} repeated value group${groupedValueCount === 1 ? '' : 's'}.`
          : 'No repeated values were found in the selected range; existing selection colors were cleared.',
        groupedValueCount,
        coloredCellCount,
        selectedCellCount: selectedCoords.length
      };
    },

    recolorLikeCellsForRange(startRow, startCol, endRow, endCol, options = {}) {
      const coords = this.coordsForRange(startRow, startCol, endRow, endCol);
      return this.recolorCoordsLikeCells(coords, options);
    },

    recolorLikeCells(options = {}) {
      const hot = this.hotInstance;
      const selected = Array.isArray(options.selection?.[0])
        ? options.selection.map(range => range.slice(0, 4))
        : hot?.getSelected();

      if (!selected || selected.length === 0) {
        alert('Please select a range to recolor.');
        return { ok: false, status: 'No selection.', groupedValueCount: 0, coloredCellCount: 0 };
      }

      const normalizedSelection = this.normalizeSelection(selected);
      const selectedCoords = this.selectionToCoords(normalizedSelection);
      return this.recolorCoordsLikeCells(selectedCoords, options);
    },


    isDarkColor(hex) {
      const rgb = parseInt(hex.slice(1), 16);
      const r = (rgb >> 16) & 0xff;
      const g = (rgb >> 8) & 0xff;
      const b = rgb & 0xff;
      const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      return luminance < 128;
    },

    isCellEditorOpen() {
      const activeEditor = this.hotInstance?.getActiveEditor?.();
      if (!activeEditor) {
        return false;
      }

      if (typeof activeEditor.isOpened === 'function' && activeEditor.isOpened()) {
        return true;
      }

      const state = String(activeEditor.state || activeEditor.editorState || '').toUpperCase();
      if (state.includes('OPEN') || state.includes('EDIT')) {
        return true;
      }

      if (typeof document !== 'undefined' && typeof Node !== 'undefined') {
        const activeElement = document.activeElement;
        const editorNodes = [
          activeEditor.TEXTAREA,
          activeEditor.textarea,
          activeEditor.input,
          activeEditor.TEXTAREA_PARENT,
          activeEditor.textareaParent,
          activeEditor.editorElement
        ].filter(node => node && typeof node.contains === 'function');

        if (activeElement instanceof Node && editorNodes.some(node => node === activeElement || node.contains(activeElement))) {
          return true;
        }
      }

      return false;
    },

    onPaste(event) {
      if (this.isCellEditorOpen()) {
        return;
      }

      event.preventDefault();

      const clipboardData = event.clipboardData || window.clipboardData;
      const pastedData = clipboardData.getData('Text');

      this.insertParsedPastedData(pastedData);
    },

    insertParsedPastedData(pastedData) {
      const trimmedData = String(pastedData || '').trim();
      if (!trimmedData) return;

      const rows = this.customSplit(trimmedData);
      const parsedData = rows.map(row => row.split('\t').map(cell => cell.trim()));

      const selection = this.hotInstance.getSelected();
      if (!selection || selection.length === 0) {
        alert('Please select a starting cell before pasting.');
        return;
      }

      const [startRow, startCol] = selection[0];

      this.hotInstance.populateFromArray(
        startRow,
        startCol,
        parsedData,
        undefined,
        undefined,
        'paste'
      );

    },

    customSplit(input) {
      const rows = [];
      let currentRow = '';
      let inQuotes = false;

      for (let i = 0; i < input.length; i += 1) {
        const currentChar = input[i];
        const nextChar = i < input.length - 1 ? input[i + 1] : '';

        currentRow += currentChar;

        if (currentChar === '"' && nextChar !== '"') {
          inQuotes = !inQuotes;
        } else if (currentChar === '\n' && !inQuotes) {
          rows.push(currentRow.trim());
          currentRow = '';
        }
      }

      if (currentRow.length > 0) {
        rows.push(currentRow.trim());
      }

      return rows.filter(row => row !== '');
    },

    addRows() {
      if (!this.hotInstance) return;

      const lastRow = Math.max(this.hotInstance.countRows() - 1, 0);
      this.hotInstance.alter('insert_row_below', lastRow, 100, 'addRows');
      this.hotInstance.render();
    },

    columnIndexToLabel(index) {
      let label = '';
      let current = Number(index);

      while (current >= 0) {
        label = String.fromCharCode((current % 26) + 65) + label;
        current = Math.floor(current / 26) - 1;
      }

      return label;
    },

    coordsToCellRef(row, col) {
      if (!Number.isInteger(row) || !Number.isInteger(col) || row < 0 || col < 0) {
        return '';
      }

      return `${this.columnIndexToLabel(col)}${row + 1}`;
    },

    rangeToFormulaRef(range) {
      if (!Array.isArray(range) || range.length < 4) {
        return '';
      }

      const [rawRow1, rawCol1, rawRow2, rawCol2] = range;
      if ([rawRow1, rawCol1, rawRow2, rawCol2].some(value => !Number.isInteger(value) || value < 0)) {
        return '';
      }

      const startRow = Math.min(rawRow1, rawRow2);
      const endRow = Math.max(rawRow1, rawRow2);
      const startCol = Math.min(rawCol1, rawCol2);
      const endCol = Math.max(rawCol1, rawCol2);
      const startRef = this.coordsToCellRef(startRow, startCol);
      const endRef = this.coordsToCellRef(endRow, endCol);

      return startRef === endRef ? startRef : `${startRef}:${endRef}`;
    },

    getActiveCell(selection = this.lastSelection) {
      const ranges = this.normalizeSelection(selection);
      if (ranges.length === 0) {
        return null;
      }

      const [rawRow, rawCol] = ranges[0];
      if (!Number.isInteger(rawRow) || !Number.isInteger(rawCol) || rawRow < 0 || rawCol < 0) {
        return null;
      }

      return {
        row: rawRow,
        col: rawCol,
        label: this.coordsToCellRef(rawRow, rawCol)
      };
    },

    getSelectionReference(selection = this.lastSelection) {
      return this.normalizeSelection(selection)
        .map(range => this.rangeToFormulaRef(range))
        .filter(Boolean)
        .join(',');
    },


    setCellSourceValue(row, col, value, source = 'formula_bar') {
      const hot = this.hotInstance;
      if (!hot || !Number.isInteger(row) || !Number.isInteger(col) || row < 0 || col < 0) {
        return false;
      }

      hot.setDataAtCell(row, col, value == null ? '' : value, source);
      hot.selectCell(row, col);
      hot.listen?.();
      return true;
    },

    focusCell(row, col) {
      const hot = this.hotInstance;
      if (!hot || !Number.isInteger(row) || !Number.isInteger(col) || row < 0 || col < 0) {
        return false;
      }

      hot.selectCell(row, col);
      hot.listen?.();
      return true;
    },

    getData() {
      return this.getComputedData();
    },

    getCellSourceValue(row, col) {
      const hot = this.hotInstance;
      if (!hot || !Number.isInteger(row) || !Number.isInteger(col)) {
        return '';
      }

      const physicalRow = typeof hot.toPhysicalRow === 'function' ? hot.toPhysicalRow(row) : row;

      if (!Number.isInteger(physicalRow) || physicalRow < 0) {
        return hot.getDataAtCell?.(row, col) ?? '';
      }

      try {
        const value = hot.getSourceDataAtCell?.(physicalRow, col);
        return value ?? '';
      } catch (error) {
        console.warn('Failed to read source cell value; falling back to rendered value.', error);
        return hot.getDataAtCell?.(row, col) ?? '';
      }
    },

    getCellDisplayValue(row, col) {
      return this.hotInstance?.getDataAtCell?.(row, col) ?? '';
    },

    setCellValue(row, col, value, source = 'edit') {
      if (!this.hotInstance || !Number.isInteger(row) || !Number.isInteger(col)) {
        return false;
      }

      this.hotInstance.setDataAtCell(row, col, value, source);
      return true;
    },

    selectCell(row, col, options = {}) {
      if (!this.hotInstance || !Number.isInteger(row) || !Number.isInteger(col)) {
        return false;
      }

      const scrollToCell = options.scrollToCell !== false;
      const changeListener = options.changeListener !== false;

      if (typeof this.hotInstance.selectCells === 'function') {
        return this.hotInstance.selectCells([[row, col, row, col]], scrollToCell, changeListener);
      }

      this.hotInstance.selectCell?.(row, col);
      return true;
    },

    loadData(data) {
      if (!this.hotInstance) {
        return;
      }

      const settings = this.hotInstance.getSettings?.() || {};
      const normalizedData = normalizeGridDataForHandsontable(data, {
        minRows: settings.minRows,
        minCols: settings.minCols
      });

      this.hotInstance.loadData(normalizedData);
    }
  }
};
</script>

<style scoped>
.spreadsheet-shell {
  display: flex;
  flex-direction: column;
  gap: 0.06rem;
  width: 100%;
  max-width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
}

.spreadsheet-frame {
  flex: 1 1 auto;
  display: flex;
  min-width: 0;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  padding: 0.22rem 0.34rem 0.22rem 0.28rem;
  border: 1px solid #d9e3ee;
  border-radius: 10px;
  overflow: hidden;
  background: #ffffff;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.85),
    0 8px 16px rgba(15, 23, 42, 0.04);
}

.spreadsheet-container {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
}

.spreadsheet-container :deep(.wtHolder) {
  box-sizing: border-box;
  padding-bottom: 0;
}

.sheet-footer {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 0;
  height: 1.16rem;
  margin: 0;
  padding: 0 0.45rem 0 0;
}

.sheet-footer__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: 1rem;
  min-height: 0;
  line-height: 1;
  border: 1px solid #94a3b8;
  background: #ffffff;
  color: #0f172a;
  border-radius: 6px;
  padding: 0 0.36rem;
  font-size: 0.64rem;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    border-color 0.18s ease;
}

.sheet-footer__action:hover {
  transform: translateY(-1px);
  border-color: #2563eb;
  box-shadow: 0 3px 8px rgba(37, 99, 235, 0.16);
}



.spreadsheet-container :deep(.handsontable td.htNoWrap:not(.wrap-text):not(.htWrap):not(.wrap-cell)) {
  white-space: nowrap !important;
  overflow: hidden;
  text-overflow: ellipsis;
}

.spreadsheet-container :deep(.handsontable td.wrap-text),
.spreadsheet-container :deep(.handsontable td.htWrap),
.spreadsheet-container :deep(.handsontable td.wrap-cell) {
  white-space: normal !important;
  word-break: break-word !important;
  overflow: hidden;
  text-overflow: clip;
  line-height: 1.2;
}



</style>

<style>
/* Case Buddy applies this document class only while its dark Advanced Workspace
   is active. Theme the component-owned frame so the spreadsheet does not retain
   a bright outline around the globally themed Handsontable canvas. */
body.case-buddy-workspace-dark .spreadsheet-frame {
  border-color: #2b3d57;
  background: #0b1728;
  box-shadow:
    inset 0 1px 0 rgba(148, 163, 184, 0.08),
    0 8px 18px rgba(0, 0, 0, 0.24);
}

body.case-buddy-workspace-dark .spreadsheet-container {
  background: #0b1728;
}

body.case-buddy-workspace-dark .sheet-footer__action {
  border-color: #3d536f;
  background: #18283f;
  color: #dce6f4;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.28);
}

body.case-buddy-workspace-dark .sheet-footer__action:hover {
  border-color: #4f87cc;
  background: #203958;
  color: #ffffff;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.34);
}
</style>
